import useGetBuildingFootprints from '@/api/queries/project/useGetBuildingFootprints';
import useGetTitleParcelAt from '@/api/queries/project/useGetTitleParcelAt';
import { Alert, Button, Card, H3, Text } from '@payaca/ambrosia';
import {
  SolarRoofView,
  type SolarLatLng,
} from '@payaca/components/solarRoofView/SolarRoofView';
import UntitledIcon from '@payaca/untitled-icons';
import { FC, useMemo, useState } from 'react';
import { parcelsMismatch } from './parcelMismatch';
import ParcelMismatchHint from './ParcelMismatchHint';
import MapAttribution from './MapAttribution';
import MapLegend from './MapLegend';

type ConfirmLocationViewProps = {
  projectId: string;
  siteAddressCentre: SolarLatLng;
  onCancel: () => void;
  isAnalysing: boolean;
  isUnavailable: boolean;
  error?: string | null;
  manualDesignError?: string | null;
  isCreatingManualDesign: boolean;
  onDrawManually: (location: SolarLatLng) => void;
  onConfirm: (location: SolarLatLng) => void;
};

/**
 * Google's building lookup snaps the raw geocode to the nearest roof, and on
 * semi-detached pairs an 8m error crosses the party wall.
 */
const ConfirmLocationView: FC<ConfirmLocationViewProps> = (props) => {
  const {
    projectId,
    siteAddressCentre,
    onCancel,
    isAnalysing,
    isUnavailable,
    error,
    manualDesignError,
    isCreatingManualDesign,
    onDrawManually,
    onConfirm,
  } = props;

  const [pinPoint, setPinPoint] = useState<SolarLatLng | null>(null);

  const { footprints } = useGetBuildingFootprints(projectId, {
    location: pinPoint ?? undefined,
  });
  const footprintRings = useMemo(
    () => footprints.map((footprint) => footprint.ring),
    [footprints]
  );

  // Omitting `location` returns the parcel at the site address (server-side).
  const { parcel: addressParcel } = useGetTitleParcelAt(projectId, null);
  const { parcel: draggedPinParcel } = useGetTitleParcelAt(
    projectId,
    pinPoint,
    { enabled: !!pinPoint }
  );
  const parcelUnderPin = pinPoint ? draggedPinParcel : addressParcel;
  const showMismatchHint =
    !isUnavailable && parcelsMismatch(addressParcel, parcelUnderPin);

  const analysisError =
    error ===
      'The roof could not be analysed at this location. Check the pin and try again, or draw the roof manually.' ||
    error === 'The roof could not be analysed at this location.'
      ? 'No roof analysis found here.'
      : error;

  return (
    <div className="relative h-full">
      <div className="absolute inset-0">
        <SolarRoofView
          apiKey={import.meta.env.VITE_GOOGLE_MAPS_KEY ?? ''}
          centre={siteAddressCentre}
          panels={[]}
          roofSegments={[]}
          panelWidthMeters={1.045}
          panelHeightMeters={1.879}
          repositionMode={!isAnalysing && !isUnavailable}
          repositionCentre={siteAddressCentre}
          onRepositionChange={setPinPoint}
          footprints={footprintRings.length ? footprintRings : undefined}
          parcelBoundary={parcelUnderPin?.ring}
          processingLabel={isAnalysing ? 'Analysing roof imagery…' : undefined}
          fill
        />
      </div>

      <Card
        sizeVariant="sm"
        // oxlint-disable-next-line payaca/no-tailwind-arbitrary-values -- No utility for calc() or min() sizing
        className="absolute left-4 top-4 w-64 max-w-[calc(100%-2rem)] shadow-lg"
      >
        <Card.Body className="space-y-3">
          <div className="space-y-1">
            <div className="flex items-center gap-2">
              <Button
                variant="quaternary"
                size="xs"
                aria-label="Back to project"
                title="Back to project"
                onClick={onCancel}
                disabled={isCreatingManualDesign}
              >
                <UntitledIcon name="arrow-left" className="h-4 w-4" />
              </Button>
              <H3>Check solar potential</H3>
            </div>
            <Text level="supporting">Place the pin on the roof.</Text>
          </div>
          {(footprintRings.length > 0 || !!parcelUnderPin) && (
            <MapLegend
              showsBuildingOutlines={footprintRings.length > 0}
              showsTitleBoundary={!!parcelUnderPin}
            />
          )}

          {showMismatchHint && <ParcelMismatchHint />}
          {isAnalysing && (
            <Text level="supporting">
              Analysing roof imagery. You can leave and return when it’s ready.
            </Text>
          )}
          {analysisError && (
            <Alert colour="yellow" className="p-3">
              {analysisError}
            </Alert>
          )}

          {isUnavailable && (
            <Alert colour="yellow" className="p-3">
              No usable satellite imagery here.
            </Alert>
          )}

          {manualDesignError && (
            <Alert colour="yellow" className="p-3">
              {manualDesignError}
            </Alert>
          )}
          <div className="flex flex-col gap-2">
            {!isUnavailable && (
              <Button
                size="sm"
                className="w-full"
                onClick={() => onConfirm(pinPoint ?? siteAddressCentre)}
                isProcessing={isAnalysing}
                disabled={isAnalysing || isCreatingManualDesign}
              >
                {error ? 'Try analysis again' : 'Analyse roof'}
              </Button>
            )}
            {(error || isUnavailable) && (
              <Button
                variant="secondary"
                size="sm"
                className="w-full"
                onClick={() => onDrawManually(pinPoint ?? siteAddressCentre)}
                isProcessing={isCreatingManualDesign}
                disabled={isAnalysing || isCreatingManualDesign}
              >
                Draw roof manually
              </Button>
            )}
          </div>
        </Card.Body>
      </Card>

      <MapAttribution
        googleData={null}
        showsBuildingOutlines={footprintRings.length > 0}
        titleBoundaryAttribution={parcelUnderPin?.attribution ?? null}
      />
    </div>
  );
};

export default ConfirmLocationView;
